import { useMemo } from 'react';

import { useAppSelector } from 'store/hooks';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import {
  BusinessNameRequest,
  BusinessNameResponse,
  InfoCompletenessScoring,
} from 'store/api/apiTypes';
import { rulesApiSlice } from 'store/api/rulesApiSlice';

/**
 * useCachedBusinessName
 * Returns cached Business Name rule data without triggering a network call.
 */
export const useCachedBusinessName = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<BusinessNameRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectRule = useMemo(
    () =>
      queryArgs
        ? rulesApiSlice.endpoints.businessName.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );
  const selectResult = useAppSelector(selectRule as any) as {
    data: any;
    status: string;
    isLoading?: boolean;
    error?: any;
  };
  const { data, status, isLoading: loading, error } = selectResult;
  const businessName = data as BusinessNameResponse | undefined;
  let passCount = 0;
  const rulesMap = businessName?.scoring_breakdown as
    | Record<string, InfoCompletenessScoring>
    | undefined;
  if (rulesMap) {
    Object.values(rulesMap).forEach(rule => {
      if (rule.score >= 8) {
        passCount += 1;
      }
    });
  }
  const isLoaded =
    !!businessName?.business_name_identified && !loading && !isGenerating;
  const isLoading = loading || isGenerating;

  return {
    businessName,
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
    companyConfidence: businessName?.business_name_identified?.confidence_level,
    companyEvidance: businessName?.business_name_identified?.evidence_strength,
    scoring: businessName?.scoring_breakdown,
    score: businessName?.final_score,
    urlsAnalyzed: businessName?.page_urls_analyzed,
    extraction: businessName?.business_name_extraction,
    identified: {
      primary: businessName?.business_name_identified?.primary_business_name,
      confidence_level:
        businessName?.business_name_identified?.confidence_level,
      evidence_strength:
        businessName?.business_name_identified?.evidence_strength,
    },
    overallAssessment: businessName?.overall_assessment,
    hasBusinessName: !!businessName,
    passed: passCount,
    rulesCount: Object.keys(businessName?.scoring_breakdown || {}).length,
  };
};
